import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { useEffect, useState } from 'react';
import { EntityTag } from '@/components/Tag/EntityTag';
import Modal from '@/components/Modal';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { TCustomQueryProps } from '@/type/hooks.types';
import { FieldValues } from 'react-hook-form';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { twMerge } from 'tailwind-merge';
import { ErrorResponse } from '@/utils/axios.http';
import { TFunction } from 'i18next';
import { TitleSection } from '../Header/Section/TitleSection';

interface EntityRelationshipsProps<TSchema extends FieldValues> {
  t: TFunction<undefined>;
  foreignKeyId: string;
  foreignKeyField: string;
  cardHeader?: React.ReactNode;
  // showButtonModalTrigger?: boolean;
  showCardActionHeader?: boolean;
  cardClassName?: string;
  cardBodyClassName?: string;
  toggleOpenModal?: boolean;
  title: string;
  searchText: string;
  mainContentNotFoundText: string;
  addButtonText: string;
  modalTitle: string;
  modalDataNotFoundText: string;
  useGetUnassignedEntities: (foreignKeyId?: string) => {
    data?: { data: TSchema[] };
    isLoading: boolean;
  };
  useGetAssignedEntities: (foreignKeyId: string) => {
    data?: { data: TSchema[] };
    isLoading: boolean;
  };
  useUpdateEntity: (
    object?: Partial<TCustomQueryProps>
  ) => UseMutationResult<
    AxiosResponse<TSchema, unknown>,
    ErrorResponse,
    TSchema,
    unknown
  >;
}

export const EntityRelationshipsManagement = <TSchema extends FieldValues>({
  t,
  foreignKeyId,
  foreignKeyField,
  cardHeader,
  // showButtonModalTrigger = true,
  showCardActionHeader = true,
  cardClassName,
  cardBodyClassName,
  toggleOpenModal,
  title,
  searchText,
  mainContentNotFoundText,
  addButtonText,
  modalTitle,
  modalDataNotFoundText,
  useGetUnassignedEntities,
  useGetAssignedEntities,
  useUpdateEntity,
}: EntityRelationshipsProps<TSchema>) => {
  useEffect(() => {
    setSelectedEntityIDs([]);
  }, [toggleOpenModal]);

  const [selectedEntityIDs, setSelectedEntityIDs] = useState<string[]>([]);

  const { data: unassignedEntities, isLoading: isLoadingUnassignedEntities } =
    useGetUnassignedEntities(foreignKeyId);

  const { data: assignedEntities, isLoading: isLoadingAssignedEntities } =
    useGetAssignedEntities(foreignKeyId);

  const { mutate: mutateUpdatedEntity, isPending: isPendingUpdatedEntity } =
    useUpdateEntity({
      t,
      showDefaultSuccessMessage: false,
      handleSuccess: () => setSelectedEntityIDs([]),
    });

  const handleAssignEntity = () => {
    if (
      unassignedEntities &&
      unassignedEntities.data &&
      unassignedEntities.data.length > 0 &&
      selectedEntityIDs.length > 0 &&
      foreignKeyId
    ) {
      for (const entity of unassignedEntities.data) {
        for (const id of selectedEntityIDs) {
          if (entity.id === id) {
            mutateUpdatedEntity({
              ...entity,
              [`${foreignKeyField}`]: foreignKeyId,
            });
          }
        }
      }
    }
  };

  const handleDeleteEntity = (entity: TSchema) => {
    mutateUpdatedEntity({
      ...entity,
      [`${foreignKeyField}`]: null,
    });
  };

  const handleSelectEntity = (id?: string) => {
    if (id) {
      if (selectedEntityIDs.includes(id)) {
        const entityIDs = selectedEntityIDs.filter(
          (selectedId: string) => selectedId !== id
        );
        setSelectedEntityIDs(entityIDs);
      } else {
        setSelectedEntityIDs((prev: string[]) => [...prev, id]);
      }
    }
  };

  const renderUnassignedEntities = () => {
    if (
      !isLoadingUnassignedEntities &&
      unassignedEntities &&
      unassignedEntities.data &&
      unassignedEntities.data.length > 0
    ) {
      return unassignedEntities.data.map((entity: TSchema) => {
        const { id, logo, name } = entity;

        return (
          <EntityTag
            key={id}
            logo={logo}
            text={name}
            selected={selectedEntityIDs.includes(`${id}`)}
            onSelectDisabled={isPendingUpdatedEntity}
            onSelect={() => handleSelectEntity(id)}
          />
        );
      });
    }
    return <></>;
  };

  const renderAssignedEntities = () => {
    if (
      !isLoadingAssignedEntities &&
      assignedEntities &&
      assignedEntities.data &&
      assignedEntities.data.length > 0
    ) {
      return assignedEntities.data.map((entity: TSchema) => {
        const { id, logo, name } = entity;

        return (
          <EntityTag
            key={id}
            logo={logo}
            text={name}
            onDeleteDisabled={isPendingUpdatedEntity}
            onDelete={() => handleDeleteEntity(entity)}
          />
        );
      });
    }

    return <></>;
  };

  return (
    <>
      <div className="relative">
        <Card
          title={<TitleSection text={title} defaultIconClassName="ki-note-2" />}
          headerElement={cardHeader}
          cardClassName={cardClassName}
          bodyClassName={twMerge('py-8 flex flex-col gap-5', cardBodyClassName)}
        >
          {showCardActionHeader && (
            <div className="flex items-center gap-3 pb-5 border-b border-b-gray-200">
              <Input
                placeholder={searchText}
                type="text"
                name="search"
                autoComplete="off"
                leftIcon={<i className="ki-outline ki-magnifier text-lg" />}
                inputWrapperClassName="w-[300px]"
                inputClassName="h-10"
              />
              <div>
                <Button
                  text={addButtonText}
                  leftIcon={<i className="ki-filled ki-plus-circle" />}
                  dataModalToggleId="entity-list-modal"
                  onClick={() => setSelectedEntityIDs([])}
                />
              </div>
            </div>
          )}
          {isLoadingAssignedEntities ? (
            <Loader />
          ) : !assignedEntities || assignedEntities?.data.length === 0 ? (
            <div className="m-10">
              <NotFound text={mainContentNotFoundText} />
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2.5">
              {renderAssignedEntities()}
            </div>
          )}
        </Card>
      </div>
      <Modal
        id="entity-list-modal"
        bodyClassName="p-2.5 m-0 min-h-[150px]"
        title={modalTitle}
        showCancelBtn={Boolean(
          unassignedEntities &&
            unassignedEntities.data &&
            unassignedEntities.data.length > 0
        )}
        showSecondBtn={true}
        secondBtnLabel={
          unassignedEntities &&
          unassignedEntities.data &&
          unassignedEntities.data.length > 0
            ? t('common:add')
            : t('common:ok')
        }
        size={
          !isLoadingUnassignedEntities && unassignedEntities?.data.length === 0
            ? 'small'
            : 'large'
        }
        onClickSecondBtn={handleAssignEntity}
        disabledSubmitButton={isPendingUpdatedEntity}
      >
        {isLoadingUnassignedEntities ? (
          <Loader />
        ) : !unassignedEntities || unassignedEntities?.data.length === 0 ? (
          <div className="w-full h-[150px] flex justify-center items-center">
            <NotFound text={modalDataNotFoundText} />
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2.5">
            {renderUnassignedEntities()}
          </div>
        )}
      </Modal>
      {isPendingUpdatedEntity && <Loader />}
    </>
  );
};
